import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import utils, { downloadBlob } from 'utils';
import { NEW_WINDOW_PROPS } from 'data/global';
import {
  CONTENT_CACHE_TIME,
  fetchFileContent,
  getFileContentQueryKey,
} from 'hooks/useFileContentQuery';

type DownloadFileProps = {
  id: string;
  url: string;
  name: string;
  version?: number;
};

/**
 * Downloads a file straight from its signed url, reusing the preview byte cache — a file
 * that was already previewed downloads instantly, and a file downloaded first warms the
 * cache for its preview.
 *
 * The data room has its own server-driven flow (`useStartDownloadingFile`); this is for
 * files that don't have one, e.g. chat attachments.
 */
export default function useDownloadFile() {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ id, url, name, version }: DownloadFileProps) => {
      if (!url) return;

      try {
        // resolves from cache when it's still fresh, otherwise fetches and caches
        const blob = await queryClient.fetchQuery({
          queryKey: getFileContentQueryKey(id, version),
          queryFn: () => fetchFileContent(url),
          staleTime: CONTENT_CACHE_TIME,
          cacheTime: CONTENT_CACHE_TIME,
        });

        downloadBlob(blob, name);
      } catch (e) {
        // the signed url may not allow cross-origin XHR — let the browser fetch it
        // instead and fall back to the server's Content-Disposition.
        utils.downloadUrl({ href: url, ...NEW_WINDOW_PROPS });
      }
    },
    [queryClient],
  );
}
